import { catchError, map, throttleTime } from 'rxjs/operators';
import { Observable, of, ReplaySubject, interval, merge, fromEvent } from 'rxjs';
// src/app/auth/auth.service.ts
import { Injectable, NgZone } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
// import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthDto, PermissionTypeEnum, SignInOutcome, UserDto } from '../../dto';
import { HttpAuthService } from './http-auth.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
	helper: JwtHelperService;
	authStateChange$ = new ReplaySubject<boolean>(1);
	timeToExpire$ = new ReplaySubject<number|'reset'>(0);
	authedState: boolean = false;

	private lastActivityTime: number = Date.now();
    private isRefreshing: boolean = false;
    private readonly REFRESH_THRESHOLD_SEC = 300;
    private readonly ACTIVITY_THRESHOLD_MS = 300000;

	constructor(
			public jwtHelper: JwtHelperService, 
			private httpAuthService: HttpAuthService,
			private toastr: ToastrService,
			private router: Router,
			private ngZone: NgZone
			) {
		this.helper = new JwtHelperService();
		this.setupActivityListeners();

		interval(1000).subscribe(() => {
            const token = this.getToken();
            if (!token) return;
            
            const newState = this.isAuthenticated();
            if (this.authedState !== newState) {
                this.authedState = newState;
                this.authStateChange$.next(newState);
            }
            
            if (newState === true) {
                const expiry = this.helper.getTokenExpirationDate(token);
                if (expiry) {
                    const secondsLeft = Math.round((expiry.getTime() - new Date().getTime()) / 1000);
                    this.timeToExpire$.next(secondsLeft);

                    // --- Auto-Refresh Logic ---
                    if (secondsLeft > 0 && secondsLeft <= this.REFRESH_THRESHOLD_SEC && !this.isRefreshing) {
                        const timeSinceLastActivity = Date.now() - this.lastActivityTime;
                        
                        // If user was active recently, refresh the token
                        if (timeSinceLastActivity <= this.ACTIVITY_THRESHOLD_MS) {
                            this.performRefresh();
                        }
                    }
                }
            }
        });
	}

	private setupActivityListeners() {
		const updateActivityTime = () => { this.lastActivityTime = Date.now(); };
		
		// Run outside Angular to prevent unnecessary change detection cycles
		this.ngZone.runOutsideAngular(() => {
			merge(
				fromEvent(window, 'mousemove'),
				fromEvent(window, 'keydown'),
				fromEvent(window, 'click'),
				fromEvent(window, 'scroll')
			)
			.pipe(throttleTime(1000))
			.subscribe(updateActivityTime);
		});
	}

	private performRefresh() {
        this.isRefreshing = true;
        this.refreshToken().subscribe({
            next: (success) => {
                this.isRefreshing = false;
                if (success) {
                    this.timeToExpire$.next('reset');
                } else {
                    // If backend returned false but didn't throw an HTTP error
                    this.applyRefreshCooldown();
                }
            },
            error: () => {
                // If network fails or 500 error, wait before trying again
                this.applyRefreshCooldown();
            }
        });
    }

	private applyRefreshCooldown() {
        // Wait 30 seconds before allowing another refresh attempt
        setTimeout(() => {
            this.isRefreshing = false;
        }, 30000); 
    }

	public isAuthenticated(): boolean {
		const storedToken = this.getToken();
		if (storedToken === undefined || storedToken == null) {
			return false;
		}
		if (this.helper.isTokenExpired(storedToken)) {
			this.signOut();
			return false;
		}
		return true;
	}

	public isVerified(): boolean {
		const token = this.getToken();
  		if (!token) {              // handle null or empty token
    		return false;
  		}
		const decodedToken = this.helper.decodeToken(token);
		return decodedToken.verified;
	}

	public getUsername(): string | null{
		const token = this.getToken();
  		if (!token) {              // handle null or empty token
    		return null;
  		}
		const decodedToken = this.helper.decodeToken(token);
		return decodedToken.username;
	}

	public hasAccess(requiredPermission: string): boolean {
		try {
			const token = this.getToken();
			if (!token) {
				return false;
			}

			try {
				const decodedToken = this.helper.decodeToken(token);
				
				// Extract the permissions array, default to empty array if undefined
				const userPermissions: string[] = decodedToken?.permissions || [];
				
				// Check for the specific permission
				return userPermissions.includes(requiredPermission);
					
			} catch (e) {
				console.error('Failed to decode token during permission check', e);
				return false;
			}
		} catch (e) {
			return false;
		}
    }

	public hasAnyAccess(requiredPermissions: string[]): boolean {
        const token = this.getToken();
        if (!token) {
            return false;
        }

        try {
            const decodedToken = this.helper.decodeToken(token);
            const userPermissions: string[] = decodedToken?.permissions || [];

            // Check if user has AT LEAST ONE of the required permissions
            return requiredPermissions.some(p => userPermissions.includes(p));

        } catch (e) {
            return false;
        }
    }

	signIn(username: string, password: string): Observable<SignInOutcome | HttpErrorResponse> {
		return this.httpAuthService.signIn(username, password).pipe(
			map(item => {
				if (item.error) {
					return {
						success: false,
						error: item.error
					}
				}
				if (item.otpRequired === true) {
                    return {
                        success: true,
                        otpRequired: true,
                        error: null
                    };
                }
				this.setToken(item.token);
				this.setUser(item.user);
				this.setLoginTime();
				this.timeToExpire$.next('reset');
				return {
					success: true,
					otpRequired: false,
					error: null
				};
			}),
			catchError(error => {
				console.log('Failed', error);
				return of({
					success: false,
					error: error
				});
			})
		);
	}
	verifyOtp(username: string, otp: string): Observable<SignInOutcome | HttpErrorResponse> {
        return this.httpAuthService.verifyOtp(username, otp).pipe(
            map((item: any) => {
                if (item.error) {
                    return {
                        success: false,
                        error: item.error
                    }
                }
                
                this.setToken(item.token);
                this.setUser(item.user);
                this.setLoginTime();
                this.timeToExpire$.next('reset');
                return {
                    success: true,
                    error: null
                };
            }),
            catchError(error => {
                console.log('Failed', error);
                return of({
                    success: false,
                    error: error
                });
            })
        );
    }
	refreshToken(): Observable<boolean> {
        return this.httpAuthService.refreshToken().pipe(
            map(item => {
                if(item && item.token) {
                    this.setToken(item.token);
                    return true;
                }
                return false;
            }),
            catchError(error => {
                console.log('Refresh Failed', error);
                return of(false);
            })
        );
    }
	signOut() {
		this.clearToken();
		this.clearLoginTime();
		this.authedState = false;
		this.authStateChange$.next(false);
		this.router.navigate(['/']);
	}

	private getToken(): string | null {
		return localStorage.getItem('auth_token');
	}
	private setToken(token: string | undefined) {
		if (token) {
			localStorage.setItem('auth_token', token);
		}
	}
	setUser(user: UserDto | undefined) {
		if (user) {
			localStorage.setItem('user', JSON.stringify(user));
		}
	}
	getUser(): UserDto | undefined {
		const stored = localStorage.getItem('user');
  		return stored ? JSON.parse(stored) as UserDto : undefined;
	}
	private clearToken() {
		localStorage.removeItem('auth_token');
	}
	private setLoginTime() {
        localStorage.setItem('login_time', new Date().toISOString());
    }
    public getLoginTime(): Date {
        const stored = localStorage.getItem('login_time');
        return stored ? new Date(stored) : new Date(); // Fallback to now if missing
    }
    private clearLoginTime() {
        localStorage.removeItem('login_time');
    }
}
